import { addDays, format, isValid, parseISO } from 'date-fns'
import { z } from 'zod'
import { hasMoreThanTwoDecimals } from '@/lib/pricing/price-vat-notice'

/**
 * Formulari de creació de reserves del calendari (runbook 6, R3; grups al runbook 7, F1).
 *
 * Un titular i una o més estades (`stays`). Amb una estada el payload és el de `POST /reservations`
 * (`buildCreateReservationPayload`); amb dues o més, el de `POST /reservations/group`
 * (`buildCreateReservationGroupPayload`). Aquest esquema només filtra els errors evidents abans
 * d'enviar: qui decideix de debò és el backend (DTOs + `junkPhoneReason` + disponibilitat).
 */

export const RESERVATION_LANGS = ['ca', 'es', 'en', 'fr', 'de', 'it', 'pt'] as const
export type ReservationLang = (typeof RESERVATION_LANGS)[number]

export const RESERVATION_LANG_LABELS: Record<ReservationLang, string> = {
  ca: 'Català',
  es: 'Castellà',
  en: 'Anglès',
  fr: 'Francès',
  de: 'Alemany',
  it: 'Italià',
  pt: 'Portuguès',
}

/** Mateixos valors que `Reservation.paymentMethod` llegeix el calendari (`ReservationCell`). */
export const RESERVATION_PAYMENT_METHODS = ['CASH', 'CARD', 'TRANSFER'] as const
export type ReservationPaymentMethod = (typeof RESERVATION_PAYMENT_METHODS)[number]

export const RESERVATION_PAYMENT_LABELS: Record<ReservationPaymentMethod, string> = {
  CASH: 'Efectiu',
  CARD: 'Targeta',
  TRANSFER: 'Transferència',
}

/** Màxim d'estades d'un grup (el DTO de `POST /reservations/group` en diu 15). */
export const MAX_GROUP_STAYS = 15

/** El preu d'una estada és un import amb cèntims sencers. */
export const PRICE_TOO_MANY_DECIMALS = 'El preu pot tenir 2 decimals com a màxim'

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** Treu espais, guions, punts i parèntesis: `+34 600 000 001` → `+34600000001`. */
export function normalizePhone(raw: string): string {
  return raw.replace(/[\s\-.()]/g, '')
}

/** Prefix internacional (`+` i una xifra que no és 0) i almenys 9 xifres més; 15 com a màxim (E.164). */
export function isPlausiblePhone(raw: string): boolean {
  return /^\+[1-9]\d{9,14}$/.test(normalizePhone(raw))
}

export function isLang(value: string | null | undefined): value is ReservationLang {
  return !!value && (RESERVATION_LANGS as readonly string[]).includes(value)
}

const emptyOr = <T extends z.ZodTypeAny>(schema: T) => schema.or(z.literal(''))

// ============================================
// UNA ESTADA
// ============================================

export const stayLineSchema = z
  .object({
    unit_id: z.string().min(1, 'Tria una habitació'),
    data_entrada: z.string().regex(DATE_RE, "La data d'entrada és obligatòria"),
    data_sortida: z.string().regex(DATE_RE, 'La data de sortida és obligatòria'),
    num_adults: z
      .number({ invalid_type_error: 'Cal un número' })
      .int('Ha de ser un nombre enter')
      .min(1, 'Mínim 1 adult')
      .max(20, 'Massa adults'),
    num_nens: z
      .number({ invalid_type_error: 'Cal un número' })
      .int('Ha de ser un nombre enter')
      .min(0, 'No pot ser negatiu')
      .max(20, 'Massa nens'),
    preu_total: z
      .number({ invalid_type_error: 'El preu és obligatori' })
      .positive('El preu ha de ser més gran que 0')
      // Més de 2 decimals: el servei el rebutjaria (plans/preus-en-crear-reserves.md, F2). Es diu aquí,
      // abans d'enviar. No té res a veure amb l'avís de l'IVA, que mai bloqueja.
      .refine((n) => !hasMoreThanTwoDecimals(n), PRICE_TOO_MANY_DECIMALS),
  })
  .refine((v) => v.data_sortida > v.data_entrada, {
    message: "La sortida ha de ser posterior a l'entrada",
    path: ['data_sortida'],
  })

export type StayLineValues = z.infer<typeof stayLineSchema>

/** Unitat i dates d'una estada: el mínim per saber si xoca amb una altra del grup. */
export type StayRange = Pick<StayLineValues, 'unit_id' | 'data_entrada' | 'data_sortida'>

/** Dues estades xoquen si són a la mateixa unitat i les nits se solapen (la sortida d'una pot ser l'entrada de l'altra). */
export function staysOverlap(a: StayRange, b: StayRange): boolean {
  if (!a.unit_id || a.unit_id !== b.unit_id) return false
  return a.data_entrada < b.data_sortida && b.data_entrada < a.data_sortida
}

/** Parells `[i, j]` (i < j) d'estades del mateix grup que xoquen entre elles. */
export function findStayOverlaps(stays: readonly StayRange[]): Array<[number, number]> {
  const pairs: Array<[number, number]> = []
  for (let i = 0; i < stays.length; i++) {
    for (let j = i + 1; j < stays.length; j++) {
      if (staysOverlap(stays[i], stays[j])) pairs.push([i, j])
    }
  }
  return pairs
}

// ============================================
// FORMULARI SENCER (titular + estades)
// ============================================

/**
 * Xarxa de seguretat: zod només executa aquest `superRefine` quan la resta del formulari és vàlida.
 * L'error en viu (encara que el nom o el preu siguin buits) el pinta `StayLineFields` amb `siblings`.
 */
export const createReservationSchema = z
  .object({
    guestFirstName: z
      .string()
      .trim()
      .min(1, 'El nom és obligatori')
      .max(60, 'Màxim 60 caràcters'),
    guestLastName: z.string().trim().max(80, 'Màxim 80 caràcters'),
    guestPhone: z
      .string()
      .trim()
      .min(1, 'El telèfon és obligatori')
      .refine(isPlausiblePhone, 'Telèfon amb prefix (+34…) i almenys 9 xifres'),
    guestEmail: emptyOr(z.string().trim().email('Correu no vàlid')),
    lang: emptyOr(z.enum(RESERVATION_LANGS)),
    /** Client triat al cercador; `''` = cap. */
    client_id: z.string(),
    metode_pagament: emptyOr(z.enum(RESERVATION_PAYMENT_METHODS)),
    hora_arribada: emptyOr(z.string().regex(TIME_RE, 'Hora no vàlida (HH:mm)')),
    notes_client: z.string().max(2000, 'Massa llarg'),
    notes_internes: z.string().max(2000, 'Massa llarg'),
    stays: z
      .array(stayLineSchema)
      .min(1, 'Cal almenys una habitació')
      .max(MAX_GROUP_STAYS, `Màxim ${MAX_GROUP_STAYS} habitacions per grup`),
  })
  .superRefine((v, ctx) => {
    for (const [i, j] of findStayOverlaps(v.stays)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Xoca amb l'habitació ${i + 1} del grup (mateixa unitat i dates)`,
        path: ['stays', j, 'unit_id'],
      })
    }
  })

export type CreateReservationFormValues = z.infer<typeof createReservationSchema>

/** L'endemà d'un `yyyy-MM-dd`, en calendari local (sense passar per UTC). */
export function nextDay(isoDate: string): string {
  const d = parseISO(isoDate)
  return isValid(d) ? format(addDays(d, 1), 'yyyy-MM-dd') : isoDate
}

/** Una estada nova: la unitat tocada, el dia tocat, 2 adults i el preu buit. */
export function emptyStay(unitId: string, checkIn: string, checkOut: string): StayLineValues {
  return {
    unit_id: unitId,
    data_entrada: checkIn,
    data_sortida: checkOut,
    num_adults: 2,
    num_nens: 0,
    // NaN i no 0: el camp surt buit i el preu s'ha d'escriure a mà.
    preu_total: Number.NaN,
  }
}

/** L'habitació que s'afegeix al grup hereta dates i ocupació de l'anterior; la unitat i el preu, a triar. */
export function stayFromPrevious(previous: StayLineValues | undefined, fallbackDate: string): StayLineValues {
  if (!previous) return emptyStay('', fallbackDate, nextDay(fallbackDate))
  return {
    unit_id: '',
    data_entrada: previous.data_entrada,
    data_sortida: previous.data_sortida,
    num_adults: Number.isFinite(previous.num_adults) ? previous.num_adults : 2,
    num_nens: Number.isFinite(previous.num_nens) ? previous.num_nens : 0,
    preu_total: Number.NaN,
  }
}

/** Valors inicials: una estada a la cel·la tocada (una nit) i el titular buit. */
export function createReservationDefaults(
  unitId: string,
  checkIn: string,
  checkOut: string,
): CreateReservationFormValues {
  return {
    guestFirstName: '',
    guestLastName: '',
    guestPhone: '',
    guestEmail: '',
    lang: '',
    client_id: '',
    metode_pagament: '',
    hora_arribada: '',
    notes_client: '',
    notes_internes: '',
    stays: [emptyStay(unitId, checkIn, checkOut)],
  }
}

/** Suma dels preus escrits (els buits no compten). */
export function staysTotal(stays: ReadonlyArray<Partial<Pick<StayLineValues, 'preu_total'>>> | undefined): number {
  if (!stays) return 0
  const cents = stays.reduce((acc, s) => {
    const p = typeof s?.preu_total === 'number' && Number.isFinite(s.preu_total) ? s.preu_total : 0
    return acc + Math.round(p * 100)
  }, 0)
  return cents / 100
}

// ============================================
// PAYLOADS
// ============================================

/** Cos de `POST /reservations` (DTO de R1). Sempre `CONFIRMED`. */
export interface CreateReservationPayload {
  unit_id: string
  data_entrada: string
  data_sortida: string
  num_adults: number
  num_nens?: number
  preu_total: number
  estat?: 'CONFIRMED'
  metode_pagament?: ReservationPaymentMethod
  notes_internes?: string
  notes_client?: string
  hora_arribada?: string
  lang?: ReservationLang
  guestPhone: string
  guestFirstName: string
  guestLastName?: string
  guestEmail?: string
  client_id?: string
}

/** Cos de `POST /reservations/group` (DTO de B1, runbook 7): de 2 a 15 estades de la mateixa propietat. */
export interface CreateReservationGroupPayload {
  holder: {
    guestFirstName: string
    guestLastName?: string
    guestPhone: string
    guestEmail?: string
    lang?: ReservationLang
    client_id?: string
  }
  stays: Array<{
    unit_id: string
    data_entrada: string
    data_sortida: string
    num_adults: number
    num_nens?: number
    preu_total: number
    notes_internes?: string
  }>
}

const optional = (s: string) => {
  const t = s.trim()
  return t === '' ? undefined : t
}

function holderOf(values: CreateReservationFormValues): CreateReservationGroupPayload['holder'] {
  return {
    guestFirstName: values.guestFirstName.trim(),
    guestLastName: optional(values.guestLastName),
    guestPhone: normalizePhone(values.guestPhone.trim()),
    guestEmail: optional(values.guestEmail),
    lang: values.lang === '' ? undefined : values.lang,
    client_id: optional(values.client_id),
  }
}

/** Valors del formulari (ja validats, una estada) → payload de `POST /reservations`. Els opcionals buits no s'envien. */
export function buildCreateReservationPayload(values: CreateReservationFormValues): CreateReservationPayload {
  const stay = values.stays[0]
  return {
    unit_id: stay.unit_id,
    data_entrada: stay.data_entrada,
    data_sortida: stay.data_sortida,
    num_adults: stay.num_adults,
    num_nens: stay.num_nens,
    preu_total: stay.preu_total,
    estat: 'CONFIRMED',
    metode_pagament: values.metode_pagament === '' ? undefined : values.metode_pagament,
    notes_internes: optional(values.notes_internes),
    notes_client: optional(values.notes_client),
    hora_arribada: optional(values.hora_arribada),
    ...holderOf(values),
  }
}

/**
 * Valors del formulari (ja validats, dues estades o més) → payload de `POST /reservations/group`.
 * El DTO del grup no porta pagament, hora d'arribada ni notes del client: no s'envien (el backend
 * rebutja els camps de més). Les notes internes van a cada estada.
 */
export function buildCreateReservationGroupPayload(
  values: CreateReservationFormValues,
): CreateReservationGroupPayload {
  const notes = optional(values.notes_internes)
  return {
    holder: holderOf(values),
    stays: values.stays.map((s) => ({
      unit_id: s.unit_id,
      data_entrada: s.data_entrada,
      data_sortida: s.data_sortida,
      num_adults: s.num_adults,
      num_nens: s.num_nens,
      preu_total: s.preu_total,
      notes_internes: notes,
    })),
  }
}
